import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  LlmRuntimeSettings,
  PmWorkerAutoscalingSettings,
  PmWorkerAutoscalingStatus,
  RunnerCacheFileEntry,
  RunnerCacheFileList,
  RunnerCacheProfile,
  RunnerCacheSettings,
} from '@claude-code-api/shared';
import { Eye, Plus, SlidersHorizontal, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface RunnerCacheResponse {
  data?: {
    settings?: RunnerCacheSettings;
    defaults?: {
      nodeMajor?: string;
      pnpmVersion?: string;
      inactiveDeleteDelayMs?: number;
    };
    versions?: string[];
    files?: RunnerCacheFileEntry[];
    total?: number;
    limit?: number;
    offset?: number;
    hasMore?: boolean;
    deletedPath?: string;
  };
  error?: string;
}

interface PmAutoscalingResponse {
  data?: PmWorkerAutoscalingStatus | { settings?: PmWorkerAutoscalingSettings };
  error?: string;
}

interface LlmRuntimeSettingsResponse {
  data?: { settings?: LlmRuntimeSettings };
  error?: string;
}

const DELETE_COUNTDOWN_SECONDS = 5;
const MS_PER_SECOND = 1000;
const CACHE_FILE_PAGE_SIZE = 50;

export default function AdminRuntimeSettingsPage() {
  const { t } = useTranslation('admin');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [settings, setSettings] = useState<RunnerCacheSettings | null>(null);
  const [pnpmVersions, setPnpmVersions] = useState<string[]>([]);
  const [defaultNodeMajor, setDefaultNodeMajor] = useState('24');
  const [newProfileName, setNewProfileName] = useState('');
  const [newProfileNodeMajor, setNewProfileNodeMajor] = useState('24');
  const [newProfilePnpmVersion, setNewProfilePnpmVersion] = useState('');
  const [createProfileOpen, setCreateProfileOpen] = useState(false);
  const [filesOpen, setFilesOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<RunnerCacheFileEntry | null>(
    null,
  );
  const [filesLoading, setFilesLoading] = useState(false);
  const [fileDeletingPath, setFileDeletingPath] = useState<string | null>(null);
  const [selectedProfile, setSelectedProfile] =
    useState<RunnerCacheProfile | null>(null);
  const [fileList, setFileList] = useState<RunnerCacheFileList>({
    files: [],
    total: 0,
    limit: CACHE_FILE_PAGE_SIZE,
    offset: 0,
    hasMore: false,
  });
  const [deleteProfile, setDeleteProfile] = useState<RunnerCacheProfile | null>(
    null,
  );
  const [deleteCountdown, setDeleteCountdown] = useState(
    DELETE_COUNTDOWN_SECONDS,
  );
  const [pmSettingsDraft, setPmSettingsDraft] =
    useState<PmWorkerAutoscalingSettings | null>(null);
  const [llmSettingsDraft, setLlmSettingsDraft] =
    useState<LlmRuntimeSettings | null>(null);
  const [llmSaving, setLlmSaving] = useState(false);
  const lastSavedPmSettingsRef = useRef<string | null>(null);
  const pmAutosaveTimerRef = useRef<number | null>(null);

  const selectableCreateVersions = useMemo(() => {
    if (!settings?.maxPnpmVersion) return pnpmVersions;
    return pnpmVersions.filter(
      (version) => compareSemver(version, settings.maxPnpmVersion) <= 0,
    );
  }, [pnpmVersions, settings?.maxPnpmVersion]);

  const fetchSettings = async () => {
    try {
      const response = await fetch('/api/admin/runtime-settings/runner-cache', {
        credentials: 'include',
      });
      const payload = (await response
        .json()
        .catch(() => ({}))) as RunnerCacheResponse;
      if (!response.ok || !payload.data?.settings) {
        throw new Error(payload.error || 'Failed to load runtime settings');
      }
      setSettings(payload.data.settings);
      const nodeMajor = String(payload.data.defaults?.nodeMajor || '24');
      setDefaultNodeMajor(nodeMajor);
      setNewProfileNodeMajor(nodeMajor);
      setNewProfilePnpmVersion(
        String(payload.data.defaults?.pnpmVersion || ''),
      );
    } catch (error) {
      console.error('Failed to load runtime settings:', error);
      toast.error(t('runtimeSettings.errors.load'));
    } finally {
      setLoading(false);
    }
  };

  const fetchPnpmVersions = async () => {
    setVersionsLoading(true);
    try {
      const response = await fetch(
        '/api/admin/runtime-settings/runner-cache/pnpm-versions',
        { credentials: 'include' },
      );
      const payload = (await response
        .json()
        .catch(() => ({}))) as RunnerCacheResponse;
      if (!response.ok || !payload.data?.versions) {
        throw new Error(payload.error || 'Failed to load pnpm versions');
      }
      setPnpmVersions(payload.data.versions);
      setNewProfilePnpmVersion(
        (current) => current || payload.data!.versions![0],
      );
    } catch (error) {
      console.error('Failed to load pnpm versions:', error);
      toast.error(t('runtimeSettings.errors.loadPnpmVersions'));
    } finally {
      setVersionsLoading(false);
    }
  };

  const fetchPmAutoscaling = async () => {
    try {
      const response = await fetch(
        '/api/admin/runtime-settings/pm-autoscaling',
        {
          credentials: 'include',
        },
      );
      const payload = (await response
        .json()
        .catch(() => ({}))) as PmAutoscalingResponse;
      if (!response.ok || !payload.data || !('settings' in payload.data)) {
        throw new Error(payload.error || 'Failed to load PM autoscaling');
      }
      const status = payload.data as PmWorkerAutoscalingStatus;
      lastSavedPmSettingsRef.current = JSON.stringify(status.settings);
      setPmSettingsDraft(status.settings);
    } catch (error) {
      console.error('Failed to load PM autoscaling:', error);
      toast.error(t('runtimeSettings.errors.loadAutoscaling'));
    }
  };

  const fetchLlmSettings = async () => {
    try {
      const response = await fetch('/api/admin/runtime-settings/llm', {
        credentials: 'include',
      });
      const payload = (await response
        .json()
        .catch(() => ({}))) as LlmRuntimeSettingsResponse;
      if (!response.ok || !payload.data?.settings) {
        throw new Error(payload.error || 'Failed to load LLM settings');
      }
      setLlmSettingsDraft(payload.data.settings);
    } catch (error) {
      console.error('Failed to load LLM settings:', error);
      toast.error(t('runtimeSettings.errors.loadLlm'));
    }
  };

  useEffect(() => {
    void fetchSettings();
    void fetchPnpmVersions();
    void fetchPmAutoscaling();
    void fetchLlmSettings();
  }, []);

  useEffect(() => {
    if (!deleteProfile) return;
    setDeleteCountdown(DELETE_COUNTDOWN_SECONDS);
    const interval = window.setInterval(() => {
      setDeleteCountdown((current) => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearInterval(interval);
  }, [deleteProfile]);

  useEffect(() => {
    if (
      selectableCreateVersions.length > 0 &&
      !selectableCreateVersions.includes(newProfilePnpmVersion)
    ) {
      setNewProfilePnpmVersion(selectableCreateVersions[0]);
    }
  }, [newProfilePnpmVersion, selectableCreateVersions]);

  const updateSettings = (next: RunnerCacheSettings) => {
    setSettings(next);
  };

  const setActiveProfile = async (profileId: string) => {
    setSaving(true);
    try {
      const response = await fetch(
        '/api/admin/runtime-settings/runner-cache/active',
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ profileId }),
        },
      );
      const payload = (await response
        .json()
        .catch(() => ({}))) as RunnerCacheResponse;
      if (!response.ok || !payload.data?.settings) {
        throw new Error(payload.error || 'Failed to switch active profile');
      }
      updateSettings(payload.data.settings);
      toast.success(t('runtimeSettings.messages.profileUpdated'));
    } catch (error) {
      console.error('Failed to switch active cache profile:', error);
      toast.error(t('runtimeSettings.errors.switchProfile'));
    } finally {
      setSaving(false);
    }
  };

  const setMaxPnpmVersion = async (maxPnpmVersion: string) => {
    setSaving(true);
    try {
      const response = await fetch(
        '/api/admin/runtime-settings/runner-cache/max-pnpm-version',
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ maxPnpmVersion }),
        },
      );
      const payload = (await response
        .json()
        .catch(() => ({}))) as RunnerCacheResponse;
      if (!response.ok || !payload.data?.settings) {
        throw new Error(payload.error || 'Failed to update max pnpm version');
      }
      updateSettings(payload.data.settings);
      await fetchPnpmVersions();
      toast.success(t('runtimeSettings.messages.pnpmVersionUpdated'));
    } catch (error) {
      console.error('Failed to update max pnpm version:', error);
      toast.error(t('runtimeSettings.errors.updatePnpmVersion'));
    } finally {
      setSaving(false);
    }
  };

  const createProfile = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        '/api/admin/runtime-settings/runner-cache/profiles',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            name: newProfileName,
            nodeMajor: newProfileNodeMajor || defaultNodeMajor,
            pnpmVersion: newProfilePnpmVersion,
          }),
        },
      );
      const payload = (await response
        .json()
        .catch(() => ({}))) as RunnerCacheResponse;
      if (!response.ok || !payload.data?.settings) {
        throw new Error(payload.error || 'Failed to create cache profile');
      }
      updateSettings(payload.data.settings);
      setNewProfileName('');
      setCreateProfileOpen(false);
      toast.success(t('runtimeSettings.messages.profileCreated'));
    } catch (error) {
      console.error('Failed to create cache profile:', error);
      toast.error(t('runtimeSettings.errors.createProfile'));
    } finally {
      setSaving(false);
    }
  };

  const openFiles = async (profile: RunnerCacheProfile, offset = 0) => {
    setSelectedProfile(profile);
    setFilesOpen(true);
    setFilesLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(CACHE_FILE_PAGE_SIZE),
        offset: String(Math.max(0, offset)),
      });
      const response = await fetch(
        `/api/admin/runtime-settings/runner-cache/profiles/${profile.id}/files?${params.toString()}`,
        { credentials: 'include' },
      );
      const payload = (await response
        .json()
        .catch(() => ({}))) as RunnerCacheResponse;
      if (!response.ok || !payload.data?.files) {
        throw new Error(payload.error || 'Failed to load cache files');
      }
      const nextFileList = {
        files: payload.data.files,
        total: Number(payload.data.total || 0),
        limit: Number(payload.data.limit || CACHE_FILE_PAGE_SIZE),
        offset: Number(payload.data.offset || 0),
        hasMore: Boolean(payload.data.hasMore),
      };
      setFileList(nextFileList);
    } catch (error) {
      setFileList({
        files: [],
        total: 0,
        limit: CACHE_FILE_PAGE_SIZE,
        offset: 0,
        hasMore: false,
      });
      console.error('Failed to load cache files:', error);
      toast.error(t('runtimeSettings.errors.loadCacheFiles'));
    } finally {
      setFilesLoading(false);
    }
  };

  const deleteCacheFile = async (file: RunnerCacheFileEntry) => {
    if (!selectedProfile) return;
    const confirmed = window.confirm(
      `Delete ${file.type} from cache?\n\n${file.path}\n\nThis cannot be undone, but package caches can be regenerated.`,
    );
    if (!confirmed) return;

    setFileDeletingPath(file.path);
    try {
      const response = await fetch(
        `/api/admin/runtime-settings/runner-cache/profiles/${selectedProfile.id}/files`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ path: file.path }),
        },
      );
      const payload = (await response
        .json()
        .catch(() => ({}))) as RunnerCacheResponse;
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to delete cache entry');
      }
      toast.success(
        t('runtimeSettings.messages.cacheEntryDeleted', {
          path: payload.data?.deletedPath || file.path,
        }),
      );
      await openFiles(selectedProfile, fileList.offset);
    } catch (error) {
      console.error('Failed to delete cache entry:', error);
      toast.error(t('runtimeSettings.errors.deleteCacheEntry'));
    } finally {
      setFileDeletingPath(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteProfile) return;
    setSaving(true);
    try {
      const response = await fetch(
        `/api/admin/runtime-settings/runner-cache/profiles/${deleteProfile.id}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ confirmProfileId: deleteProfile.id }),
        },
      );
      const payload = (await response
        .json()
        .catch(() => ({}))) as RunnerCacheResponse;
      if (!response.ok || !payload.data?.settings) {
        throw new Error(payload.error || 'Failed to delete cache profile');
      }
      updateSettings(payload.data.settings);
      setDeleteProfile(null);
      toast.success(t('runtimeSettings.messages.profileDeleted'));
    } catch (error) {
      console.error('Failed to delete cache profile:', error);
      toast.error(t('runtimeSettings.errors.deleteProfile'));
    } finally {
      setSaving(false);
    }
  };

  const savePmAutoscaling = async (
    nextSettings = pmSettingsDraft,
    options: { silent?: boolean } = {},
  ) => {
    if (!nextSettings) return;
    setSaving(true);
    try {
      const response = await fetch(
        '/api/admin/runtime-settings/pm-autoscaling',
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(nextSettings),
        },
      );
      const payload = (await response
        .json()
        .catch(() => ({}))) as PmAutoscalingResponse;
      const settings =
        payload.data && 'settings' in payload.data
          ? payload.data.settings
          : undefined;
      if (!response.ok || !settings) {
        throw new Error(payload.error || 'Failed to save PM autoscaling');
      }
      lastSavedPmSettingsRef.current = JSON.stringify(settings);
      setPmSettingsDraft(settings);
      await fetchPmAutoscaling();
      if (!options.silent) {
        toast.success(t('runtimeSettings.messages.autoscalingSaved'));
      }
    } catch (error) {
      console.error('Failed to save PM autoscaling:', error);
      toast.error(t('runtimeSettings.errors.saveAutoscaling'));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!pmSettingsDraft) return;

    const serialized = JSON.stringify(pmSettingsDraft);
    if (serialized === lastSavedPmSettingsRef.current) return;

    if (pmAutosaveTimerRef.current) {
      window.clearTimeout(pmAutosaveTimerRef.current);
    }

    pmAutosaveTimerRef.current = window.setTimeout(() => {
      void savePmAutoscaling(pmSettingsDraft, { silent: true });
    }, 700);

    return () => {
      if (pmAutosaveTimerRef.current) {
        window.clearTimeout(pmAutosaveTimerRef.current);
      }
    };
  }, [pmSettingsDraft]);

  const updatePmSettingsDraft = (
    patch: Partial<PmWorkerAutoscalingSettings>,
  ) => {
    setPmSettingsDraft((current) =>
      current ? { ...current, ...patch } : current,
    );
  };

  const saveLlmSettings = async () => {
    if (!llmSettingsDraft) return;
    setLlmSaving(true);
    try {
      const response = await fetch('/api/admin/runtime-settings/llm', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          teamTaskTokenBudget: llmSettingsDraft.teamTaskTokenBudget,
          acpxPmMaxOutputTokens: llmSettingsDraft.acpxPmMaxOutputTokens,
          teamAiPmMaxOutputTokens: llmSettingsDraft.teamAiPmMaxOutputTokens,
          reservationTtlMs: llmSettingsDraft.reservationTtlMs,
          degradedUserRateLimitPerMinute:
            llmSettingsDraft.degradedUserRateLimitPerMinute,
        }),
      });
      const payload = (await response
        .json()
        .catch(() => ({}))) as LlmRuntimeSettingsResponse;
      if (!response.ok || !payload.data?.settings) {
        throw new Error(payload.error || 'Failed to save LLM settings');
      }
      setLlmSettingsDraft(payload.data.settings);
      toast.success(t('runtimeSettings.messages.llmSaved'));
    } catch (error) {
      console.error('Failed to save LLM settings:', error);
      toast.error(t('runtimeSettings.errors.saveLlm'));
    } finally {
      setLlmSaving(false);
    }
  };

  const canDelete = (profile: RunnerCacheProfile) => {
    if (!settings || profile.id === settings.activeProfileId) return false;
    const inactiveSince = profile.lastDeactivatedAt || profile.createdAt;
    return (
      Date.now() - new Date(inactiveSince).getTime() >= 24 * 60 * 60 * 1000
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Runtime settings"
        description="Manage shared runner package caches."
      />

      <section className="space-y-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="flex items-center gap-3">
            <div className="space-y-1">
              <h2 className="text-base font-semibold">Runner package caches</h2>
              <p className="text-sm text-muted-foreground">
                New runner sessions mount the active npm and pnpm cache volumes.
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => setCreateProfileOpen(true)}
              disabled={loading || versionsLoading}
            >
              <Plus />
              Add
            </Button>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Active profile</Label>
              <Select
                value={settings?.activeProfileId || ''}
                onValueChange={setActiveProfile}
                disabled={loading || saving || !settings}
              >
                <SelectTrigger className="w-[260px]">
                  <SelectValue placeholder="Select active profile" />
                </SelectTrigger>
                <SelectContent>
                  {settings?.profiles.map((profile) => (
                    <SelectItem key={profile.id} value={profile.id}>
                      {profile.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Max pnpm version</Label>
              <Select
                value={settings?.maxPnpmVersion || ''}
                onValueChange={setMaxPnpmVersion}
                disabled={loading || saving || versionsLoading}
              >
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="Select max version" />
                </SelectTrigger>
                <SelectContent>
                  {pnpmVersions.map((version) => (
                    <SelectItem key={version} value={version}>
                      {version}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-md border border-border/70">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Profile</TableHead>
                <TableHead>Runtime</TableHead>
                <TableHead>Volumes</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {settings?.profiles.map((profile) => (
                <TableRow key={profile.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{profile.name}</span>
                      {profile.id === settings.activeProfileId && (
                        <Badge variant="success">Active</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    Node {profile.nodeMajor} / pnpm {profile.pnpmVersion}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1 font-mono text-xs">
                      <div>{profile.npmCacheVolume}</div>
                      <div>{profile.pnpmStoreVolume}</div>
                    </div>
                  </TableCell>
                  <TableCell>{formatDate(profile.createdAt)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openFiles(profile)}
                      >
                        <Eye />
                        Files
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={!canDelete(profile)}
                        onClick={() => setDeleteProfile(profile)}
                      >
                        <Trash2 />
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      <section className="border-t border-border/70 pt-6">
        <Card className="overflow-hidden shadow-sm">
          <CardHeader className="flex-row items-center justify-between gap-4 border-b border-border/70 bg-muted/25 px-4 py-3">
            <div className="flex items-center gap-3">
              <SlidersHorizontal className="h-5 w-5 text-primary" />
              <div>
                <CardTitle className="text-base font-semibold">
                  Policy Configuration
                </CardTitle>
                <CardDescription className="mt-1 text-xs">
                  Shared PM worker pool. Policy changes apply live.
                </CardDescription>
              </div>
            </div>
            {pmSettingsDraft && (
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Autoscaling
                </span>
                <Switch
                  checked={pmSettingsDraft.enabled}
                  onCheckedChange={(enabled) =>
                    updatePmSettingsDraft({ enabled })
                  }
                />
              </div>
            )}
          </CardHeader>

          {pmSettingsDraft && (
            <CardContent className="space-y-5 p-4">
              <div className="grid gap-4 lg:grid-cols-[1.1fr_0.55fr_0.55fr]">
                <ReplicaRangeControl
                  min={pmSettingsDraft.minReplicas}
                  max={pmSettingsDraft.maxReplicas}
                  onChange={(minReplicas, maxReplicas) =>
                    updatePmSettingsDraft({ minReplicas, maxReplicas })
                  }
                />
                <PolicyNumberField
                  label="Jobs per replica"
                  value={pmSettingsDraft.jobsPerReplica}
                  min={1}
                  suffix="jobs"
                  onChange={(jobsPerReplica) =>
                    updatePmSettingsDraft({ jobsPerReplica })
                  }
                />
                <PolicyNumberField
                  label="Worker concurrency"
                  value={pmSettingsDraft.workerConcurrency}
                  min={1}
                  suffix="jobs"
                  onChange={(workerConcurrency) =>
                    updatePmSettingsDraft({ workerConcurrency })
                  }
                />
              </div>

              <div className="grid gap-4 md:grid-cols-4">
                <PolicyNumberField
                  label="Max team sessions"
                  value={pmSettingsDraft.teamMaxConcurrentAgents}
                  min={1}
                  max={10}
                  suffix="sessions"
                  onChange={(teamMaxConcurrentAgents) =>
                    updatePmSettingsDraft({ teamMaxConcurrentAgents })
                  }
                />
                <PolicyNumberField
                  label="Poll interval"
                  value={msToSeconds(pmSettingsDraft.pollMs)}
                  min={1}
                  suffix="sec"
                  onChange={(seconds) =>
                    updatePmSettingsDraft({
                      pollMs: secondsToMs(seconds),
                    })
                  }
                />
                <PolicyNumberField
                  label="Scale cooldown"
                  value={msToSeconds(pmSettingsDraft.cooldownMs)}
                  min={1}
                  suffix="sec"
                  onChange={(seconds) =>
                    updatePmSettingsDraft({
                      cooldownMs: secondsToMs(seconds),
                    })
                  }
                />
                <PolicyNumberField
                  label="Idle scale-down"
                  value={msToSeconds(pmSettingsDraft.idleScaleDownMs)}
                  min={1}
                  suffix="sec"
                  onChange={(seconds) =>
                    updatePmSettingsDraft({
                      idleScaleDownMs: secondsToMs(seconds),
                    })
                  }
                />
              </div>

              <p className="text-xs text-muted-foreground">
                Set min replicas to 0 to remove idle PM workers after the idle
                scale-down time. Max team sessions is the platform-wide Team AI
                session limit. Worker concurrency applies to new or restarted PM
                workers.
              </p>
            </CardContent>
          )}
        </Card>
      </section>

      <section className="border-t border-border/70 pt-6">
        <Card className="overflow-hidden shadow-sm">
          <CardHeader className="border-b border-border/70 bg-muted/25 px-4 py-3">
            <CardTitle className="text-base font-semibold">
              AI usage safeguards
            </CardTitle>
            <CardDescription className="mt-1 text-xs">
              Global limits apply live to new model requests on every API
              replica.
            </CardDescription>
          </CardHeader>
          {llmSettingsDraft && (
            <CardContent className="space-y-4 p-4">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                <PolicyNumberField
                  label="Tokens per team task"
                  value={llmSettingsDraft.teamTaskTokenBudget}
                  min={10_000}
                  max={10_000_000}
                  suffix="tokens"
                  onChange={(teamTaskTokenBudget) =>
                    setLlmSettingsDraft((current) =>
                      current ? { ...current, teamTaskTokenBudget } : current,
                    )
                  }
                />
                <PolicyNumberField
                  label="ACPX PM tool output"
                  value={llmSettingsDraft.acpxPmMaxOutputTokens}
                  min={512}
                  max={32_768}
                  suffix="tokens"
                  onChange={(acpxPmMaxOutputTokens) =>
                    setLlmSettingsDraft((current) =>
                      current ? { ...current, acpxPmMaxOutputTokens } : current,
                    )
                  }
                />
                <PolicyNumberField
                  label="Team AI PM output limit"
                  value={llmSettingsDraft.teamAiPmMaxOutputTokens}
                  min={512}
                  max={32_768}
                  suffix="tokens"
                  onChange={(teamAiPmMaxOutputTokens) =>
                    setLlmSettingsDraft((current) =>
                      current
                        ? { ...current, teamAiPmMaxOutputTokens }
                        : current,
                    )
                  }
                />
                <PolicyNumberField
                  label="Reservation lifetime"
                  value={Math.round(llmSettingsDraft.reservationTtlMs / 60_000)}
                  min={1}
                  max={1_440}
                  suffix="min"
                  onChange={(minutes) =>
                    setLlmSettingsDraft((current) =>
                      current
                        ? { ...current, reservationTtlMs: minutes * 60_000 }
                        : current,
                    )
                  }
                />
                <PolicyNumberField
                  label="Redis-outage user limit"
                  value={llmSettingsDraft.degradedUserRateLimitPerMinute}
                  min={1}
                  max={10_000}
                  suffix="req/min"
                  onChange={(degradedUserRateLimitPerMinute) =>
                    setLlmSettingsDraft((current) =>
                      current
                        ? { ...current, degradedUserRateLimitPerMinute }
                        : current,
                    )
                  }
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <p className="text-xs text-muted-foreground">
                  PM output limits apply to new planning calls. Requests reserve
                  their maximum token and credit cost, then refund unused
                  capacity after settlement.
                </p>
                <Button onClick={saveLlmSettings} disabled={llmSaving}>
                  {llmSaving ? 'Saving…' : 'Save safeguards'}
                </Button>
              </div>
            </CardContent>
          )}
        </Card>
      </section>

      <Dialog open={createProfileOpen} onOpenChange={setCreateProfileOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add cache profile</DialogTitle>
            <DialogDescription>
              Create a versioned npm and pnpm cache volume pair.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="profileName">Profile name</Label>
              <Input
                id="profileName"
                value={newProfileName}
                onChange={(event) => setNewProfileName(event.target.value)}
                placeholder="Node 24 / pnpm 10.33.2"
                disabled={saving}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nodeMajor">Node major</Label>
              <Input
                id="nodeMajor"
                value={newProfileNodeMajor}
                onChange={(event) => setNewProfileNodeMajor(event.target.value)}
                disabled={saving}
              />
            </div>
            <div className="space-y-2">
              <Label>pnpm version</Label>
              <Select
                value={newProfilePnpmVersion}
                onValueChange={setNewProfilePnpmVersion}
                disabled={saving || versionsLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select pnpm" />
                </SelectTrigger>
                <SelectContent>
                  {selectableCreateVersions.map((version) => (
                    <SelectItem key={version} value={version}>
                      {version}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setCreateProfileOpen(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={createProfile}
                disabled={saving || !newProfilePnpmVersion}
              >
                <Plus />
                Add
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={filesOpen} onOpenChange={setFilesOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{selectedProfile?.name || 'Cache files'}</DialogTitle>
            <DialogDescription>
              Browse cache files page by page. Delete corrupted cache entries
              only when needed; package managers can regenerate them.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
            <span>
              Showing{' '}
              {fileList.total === 0
                ? '0'
                : `${fileList.offset + 1}-${Math.min(fileList.offset + fileList.files.length, fileList.total)}`}{' '}
              of {fileList.total}
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={
                  filesLoading || fileList.offset <= 0 || !selectedProfile
                }
                onClick={() =>
                  selectedProfile &&
                  openFiles(
                    selectedProfile,
                    Math.max(0, fileList.offset - fileList.limit),
                  )
                }
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={filesLoading || !fileList.hasMore || !selectedProfile}
                onClick={() =>
                  selectedProfile &&
                  openFiles(selectedProfile, fileList.offset + fileList.limit)
                }
              >
                Next
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={filesLoading || !selectedProfile}
                onClick={() =>
                  selectedProfile && openFiles(selectedProfile, fileList.offset)
                }
              >
                Refresh
              </Button>
            </div>
          </div>
          <div className="max-h-[520px] overflow-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Path</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Modified</TableHead>
                  <TableHead className="text-right">Manage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filesLoading ? (
                  <TableRow>
                    <TableCell colSpan={5}>Loading...</TableCell>
                  </TableRow>
                ) : fileList.files.length > 0 ? (
                  fileList.files.map((file) => (
                    <TableRow key={`${file.path}-${file.modifiedAt || ''}`}>
                      <TableCell className="max-w-[24rem] font-mono text-xs">
                        <button
                          type="button"
                          className="block max-w-full truncate text-left hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          title="View cache file details"
                          onClick={() => setSelectedFile(file)}
                        >
                          {file.path}
                        </button>
                      </TableCell>
                      <TableCell>{file.type}</TableCell>
                      <TableCell>{formatBytes(file.size)}</TableCell>
                      <TableCell>{formatDate(file.modifiedAt)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={fileDeletingPath === file.path}
                          onClick={() => deleteCacheFile(file)}
                        >
                          {fileDeletingPath === file.path
                            ? 'Deleting...'
                            : 'Delete'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5}>No files found.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={selectedFile !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedFile(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cache file details</DialogTitle>
            <DialogDescription>
              Full path and metadata for this runner cache entry.
            </DialogDescription>
          </DialogHeader>
          {selectedFile && (
            <dl className="grid gap-3 text-sm">
              <div>
                <dt className="text-muted-foreground">Path</dt>
                <dd className="mt-1 break-all rounded-md bg-muted p-3 font-mono text-xs">
                  {selectedFile.path}
                </dd>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <dt className="text-muted-foreground">Type</dt>
                  <dd className="mt-1 font-medium">{selectedFile.type}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Size</dt>
                  <dd className="mt-1 font-medium">
                    {formatBytes(selectedFile.size)}
                  </dd>
                </div>
              </div>
              <div>
                <dt className="text-muted-foreground">Modified</dt>
                <dd className="mt-1 font-medium">
                  {formatDate(selectedFile.modifiedAt)}
                </dd>
              </div>
            </dl>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(deleteProfile)}
        onOpenChange={() => setDeleteProfile(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete cache profile</DialogTitle>
            <DialogDescription>
              This removes the inactive profile and its Docker cache volumes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-border/70 p-3 font-mono text-xs">
              {deleteProfile?.npmCacheVolume}
              <br />
              {deleteProfile?.pnpmStoreVolume}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteProfile(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={saving || deleteCountdown > 0}
                onClick={confirmDelete}
              >
                {deleteCountdown > 0
                  ? `Delete in ${deleteCountdown}s`
                  : 'Delete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatDate(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
}

function formatBytes(value?: number | null) {
  if (!Number.isFinite(value || NaN)) return '-';
  const bytes = Number(value);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function compareSemver(left: string, right: string) {
  const a = left.split('.').map((part) => Number(part));
  const b = right.split('.').map((part) => Number(part));
  for (let index = 0; index < 3; index += 1) {
    const delta = (a[index] || 0) - (b[index] || 0);
    if (delta !== 0) return delta;
  }
  return 0;
}

function msToSeconds(value: number) {
  return Math.max(1, Math.round(Number(value || 0) / MS_PER_SECOND));
}

function secondsToMs(value: number) {
  return Math.max(1, Math.round(Number(value || 1))) * MS_PER_SECOND;
}

function ReplicaRangeControl({
  min,
  max,
  onChange,
}: {
  min: number;
  max: number;
  onChange: (min: number, max: number) => void;
}) {
  const sliderMax = Math.max(10, max, min + 1);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <Label className="text-sm font-medium">Replica range</Label>
        <div className="flex items-center gap-2 text-sm font-semibold text-primary">
          <span className="rounded bg-primary/10 px-2 py-0.5">{min}</span>
          <span className="text-muted-foreground">-</span>
          <span className="rounded bg-primary/10 px-2 py-0.5">{max}</span>
        </div>
      </div>
      <Slider
        min={0}
        max={sliderMax}
        step={1}
        minStepsBetweenThumbs={1}
        value={[Math.min(min, max), Math.max(max, min)]}
        onValueChange={([minReplicas = 0, maxReplicas = sliderMax]) =>
          onChange(minReplicas, maxReplicas)
        }
      />
    </div>
  );
}

function PolicyNumberField({
  label,
  value,
  min = 0,
  max,
  suffix,
  onChange,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  suffix: string;
  onChange: (value: number) => void;
}) {
  const clamp = (next: number) => {
    const lowerBounded = Math.max(min, next);
    return typeof max === 'number' ? Math.min(max, lowerBounded) : lowerBounded;
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex h-9 items-center overflow-hidden rounded-md border border-input bg-background">
        <button
          type="button"
          className="h-full px-3 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
          onClick={() => onChange(clamp(Number(value || 0) - 1))}
          aria-label={`Decrease ${label}`}
        >
          -
        </button>
        <Input
          type="number"
          min={min}
          max={max}
          value={value}
          onChange={(event) =>
            onChange(clamp(Number(event.target.value) || min))
          }
          className="h-full flex-1 border-0 text-center text-sm font-medium shadow-none focus-visible:ring-0"
        />
        <span className="min-w-12 px-3 text-right text-xs font-medium uppercase text-muted-foreground">
          {suffix}
        </span>
      </div>
    </div>
  );
}
